import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FOCUSABLE_SELECTORS } from 'src/components/common/items-navigator';

/**
 * @typedef {Pick<import('antd').PopconfirmProps, 'onConfirm' | 'onCancel' | 'onOpenChange'> & {
 *   overlayClassName?: string
 * }} UseAccessiblePopconfirmProps
 */

/**
 * Hook to make Ant Design Popconfirm fully accessible
 * @param {UseAccessiblePopconfirmProps} params
 * @returns {import('antd').PopconfirmProps}
 */
export const useAccessiblePopconfirm = ({
  onConfirm,
  onCancel,
  onOpenChange,
  overlayClassName = '',
} = {}) => {
  const [open, setOpen] = useState(false);
  const popconfirmContentRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Generate a unique ID for this specific popconfirm instance
  const instanceId = useId();
  const sanitizedId = `popconfirm-${instanceId.replace(/:/g, '')}`;
  const uniqueClassName = overlayClassName ? `${overlayClassName} ${sanitizedId}` : sanitizedId;

  // Handle open/close state changes
  const handleOpenChange = useCallback(
    newOpen => {
      setOpen(newOpen);

      if (newOpen) {
        previousFocusRef.current = document.activeElement;
      }

      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  // Move focus to first element when popconfirm opens
  useEffect(() => {
    if (!open) return;

    requestAnimationFrame(() => {
      // Find THIS specific popconfirm by unique class
      const popconfirmElement = document.querySelector(
        `.${sanitizedId}:not(.ant-popconfirm-hidden)`,
      );

      if (popconfirmElement) {
        popconfirmContentRef.current = popconfirmElement;

        const focusableElements = popconfirmElement.querySelectorAll(
          FOCUSABLE_SELECTORS.join(', '),
        );

        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    });
  }, [open, sanitizedId]);

  const focusPreviousElement = () => {
    requestAnimationFrame(() => {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    });
  };

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        handleOpenChange(false);

        focusPreviousElement();
        return;
      }

      if (e.key === 'Tab' && popconfirmContentRef.current) {
        const focusableElements = popconfirmContentRef.current.querySelectorAll(
          FOCUSABLE_SELECTORS.join(', '),
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        const isInsidePopconfirm = popconfirmContentRef.current.contains(activeElement);

        if (!isInsidePopconfirm) {
          e.preventDefault();
          firstElement.focus();
          return;
        }

        if (e.shiftKey) {
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, handleOpenChange]);

  const handleCancel = useCallback(
    e => {
      onCancel?.(e);
      setOpen(false);

      focusPreviousElement();
    },
    [onCancel],
  );

  const handleConfirm = useCallback(
    e => {
      onConfirm?.(e);
      setOpen(false);

      focusPreviousElement();
    },
    [onConfirm],
  );

  return {
    open,
    onOpenChange: handleOpenChange,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    overlayClassName: uniqueClassName,
  };
};
